import {useState} from 'react'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAnalytics, useToast, useWishlist} from '_customHooks'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isUserLogged, updateCloudinarySize, WishlistError} from '_utils'
import {FullProductType, ProductType} from '_types'

/**
 * @name useWishlistActions
 *
 * @hook
 *
 * @description
 * Custom hook to manage wishlist actions.
 *
 * @returns An object containing wishlist data and functions to manage wishlist items.
 */
const useWishlistActions = () => {
    const toast = useToast()
    const {data: customer} = useCurrentCustomer()
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const customerId = useCustomerId()
    const {analyticsEventWishlist} = useAnalytics()
    const {publicWishlistId, privateWishlistId, getProductPrivateWishlistId, getProductPublicWishlistId, refetchWishlists} = useWishlist()

    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation('createCustomerProductListItem')
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation('deleteCustomerProductListItem')

    const [isActionLoading, setIsActionLoading] = useState<boolean>(false)

    const productAddedLabel = formatMessage({id: 'wishlist.addtowishlist.success.msg', defaultMessage: 'This product has been added to the Wishlist'})
    const productRemovedLabel = formatMessage({
        id: 'wishlist.removeproduct.success.msg',
        defaultMessage: 'This product has been removed from the Wishlist',
    })
    const productExistLabel = formatMessage({id: 'wishlist.addtowishlist.exist.msg', defaultMessage: 'The product is already saved in your Wishlist'})

    /**
     * Adds a product to the wishlist.
     *
     * @throws {WishlistError} If `privateWishlistId` or `publicWishlistId` values are not defined.
     * @param {Object} product - The product to be added to the wishlist.
     * @param {Object} [selectedVariant] - The ID of the selected variant.
     * @returns {Promise<void>} A promise that resolves when the product is added.
     */
    const addItemToWishlist = async (product, selectedVariant?): Promise<void> => {
        setIsActionLoading(true)

        const productId = product?.productId || product?.id

        try {
            if (!privateWishlistId) throw new WishlistError(`Can't add item to wishlist because privateWishlistId value is undefined.`)
            if (!publicWishlistId) throw new WishlistError(`Can't add item to wishlist because publicWishlistId value is undefined.`)

            await createCustomerProductListItem({
                parameters: {customerId: customerId || '', listId: privateWishlistId, c_locale: locale.id},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: selectedVariant || productId,
                },
            })

            await createCustomerProductListItem({
                parameters: {customerId: customerId || '', listId: publicWishlistId, c_locale: locale.id},
                body: {
                    quantity: 1,
                    public: true,
                    priority: 1,
                    type: 'product',
                    productId: selectedVariant || productId,
                },
            })

            // Refresh the wishlist lists in THIS hook instance so a subsequent remove (e.g. add→remove
            // entirely inside the zoom, without leaving the modal) can resolve the just-added item's
            // line-item id. removeItemFromWishlist reads getProductPrivate/PublicWishlistId, which derive
            // from these lists; without an awaited refetch the item isn't in the cache yet, so the delete
            // finds nothing (the "removed" toast still fires but the heart stays filled).
            await refetchWishlists()

            const productVariations = product?.c_variationGroupsData
            const activeVariation = productVariations?.find((variation) => variation.default)

            const productName = product?.c_shortDescription
            const productImageSrc = activeVariation?.images?.large?.[0]?.url || activeVariation?.images?.url || product?.c_images?.large?.[0]?.url
            const productImage = updateCloudinarySize(productImageSrc, 400)

            toast({
                title: productName,
                status: 'success',
                productImg: productImage,
                actionLabel: productAddedLabel,
                isUserLogged: isUserLogged(customer),
            })

            analyticsEventWishlist({
                action: 'add',
                label: selectedVariant || productId,
            })
        } catch (error) {
            console.error('Failed to add item to wishlist:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    /**
     * Removes a product from the wishlist.
     *
     * @throws {WishlistError} If `privateWishlistId` or `publicWishlistId` values are not defined.
     * @param {string} productId - The product to be removed from the wishlist.
     * @param {FullProductType | ProductType} [product] - The product to be removed from the wishlists (used only for the toast).
     * @returns {Promise<void>} A promise that resolves when the product is removed.
     */
    const removeItemFromWishlist = async (productId: string, product?: FullProductType | ProductType): Promise<void> => {
        setIsActionLoading(true)

        const privateProductId = getProductPrivateWishlistId(productId)
        const publicProductId = getProductPublicWishlistId(productId)

        try {
            if (!privateWishlistId) throw new WishlistError(`Can't remove item from wishlist because privateWishlistId value is undefined.`)
            if (!publicWishlistId) throw new WishlistError(`Can't remove item from wishlist because publicWishlistId value is undefined.`)

            if (privateProductId) {
                await deleteCustomerProductListItem({
                    parameters: {customerId: customerId || '', listId: privateWishlistId, itemId: privateProductId, c_locale: locale.id},
                })
            }

            if (publicProductId) {
                await deleteCustomerProductListItem({
                    parameters: {customerId: customerId || '', listId: publicWishlistId, itemId: publicProductId, c_locale: locale.id},
                })
            }

            // Keep this instance's lists fresh after the delete (symmetry with addItemToWishlist), so an
            // immediate follow-up action inside the zoom resolves against up-to-date data.
            await refetchWishlists()

            analyticsEventWishlist({
                action: 'remove',
                label: productId,
            })

            if (product) {
                const productVariations = product?.c_variationGroupsData
                const activeVariation = productVariations?.find((variation) => variation.default)

                const productName = product?.c_shortDescription
                const productImageSrc = activeVariation?.images?.large?.[0]?.url || activeVariation?.images?.url || product?.c_images?.large?.[0]?.url
                const productImage = updateCloudinarySize(productImageSrc, 400)

                toast({
                    title: productName,
                    status: 'success',
                    productImg: productImage,
                    actionLabel: productRemovedLabel,
                    isUserLogged: isUserLogged(customer),
                })
            }
        } catch (error) {
            console.error('Failed to remove item from wishlist:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    /**
     * Allerts the user that the product is already in wishlist.
     *
     * @param {ProductType} product - The product to be shown in the toast.
     */
    const productAlreadyInWishlist = (product: ProductType): void => {
        const productVariations = product?.c_variationGroupsData
        const activeVariation = productVariations?.find((variation) => variation.default)

        const productName = product?.c_shortDescription
        const productImageSrc = activeVariation?.images?.large?.[0]?.url || activeVariation?.images?.url || product?.c_images?.large[0]?.url
        const productImage = updateCloudinarySize(productImageSrc, 400)

        toast({
            title: productName,
            status: 'error',
            productImg: productImage,
            actionLabel: productExistLabel,
        })
    }

    return {
        addItemToWishlist,
        removeItemFromWishlist,
        productAlreadyInWishlist,
        isActionLoading,
    }
}

export {useWishlistActions}
